import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import {
  beginProgrammaticScroll,
  endProgrammaticScroll,
} from "./scroll-helpers.js";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

/** Matches `respond-min($screen-sm)` in SCSS ($screen-sm 768 → min-width 769px). */
const STAGE_DESKTOP_MQ = "(min-width: 769px)";

export function initStage(section) {
  if (section.dataset.stageInitialized === "true") return;
  section.dataset.stageInitialized = "true";

  const track = section.querySelector("[data-stage-track]");
  const slides = gsap.utils.toArray(
    section.querySelectorAll("[data-stage-item]"),
  );
  const prevBtns = section.querySelectorAll("[data-stage-prev]");
  const nextBtns = section.querySelectorAll("[data-stage-next]");
  const count = slides.length;

  if (!track || count < 2) return;

  const stepCount = count - 1;

  function resetMobileLayout() {
    gsap.killTweensOf(window);
    gsap.set(track, { clearProps: "transform" });
    gsap.set(slides, { clearProps: "all" });
    slides.forEach((slide, i) => {
      slide.classList.toggle("is-active", i === 0);
    });
    prevBtns.forEach((btn) => {
      btn.disabled = false;
    });
    nextBtns.forEach((btn) => {
      btn.disabled = false;
    });
  }

  if (!window.matchMedia(STAGE_DESKTOP_MQ).matches) {
    resetMobileLayout();
  }

  ScrollTrigger.matchMedia({
    [STAGE_DESKTOP_MQ]: () => {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const ease = reducedMotion ? "none" : "power2.inOut";
      const navScrollDuration = reducedMotion ? 0 : 1;

      const clamp01 = (value) => gsap.utils.clamp(0, 1, value);

      let activeIndex = 0;

      function renderStage(progress) {
        const p = clamp01(progress);
        const w = section.offsetWidth;
        gsap.set(track, { x: -p * stepCount * w });
      }

      function updateNav(progress) {
        const idx = Math.round(clamp01(progress) * stepCount);
        activeIndex = idx;

        prevBtns.forEach((btn) => (btn.disabled = idx === 0));

        slides.forEach((slide, i) => {
          slide.classList.toggle("is-active", i === idx);
        });
      }

      const st = ScrollTrigger.create({
        trigger: section,
        pin: true,
        end: () => "+=" + window.innerHeight * stepCount,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          renderStage(self.progress);
          updateNav(self.progress);
        },
      });

      const ac = new AbortController();
      const { signal } = ac;

      function goTo(targetIndex) {
        const scrollStep = (st.end - st.start) / stepCount;
        const scrollTarget = st.start + scrollStep * targetIndex;

        gsap.killTweensOf(window);
        endProgrammaticScroll();

        beginProgrammaticScroll();
        gsap.to(window, {
          scrollTo: { y: scrollTarget, autoKill: false },
          duration: navScrollDuration,
          ease,
          onComplete: endProgrammaticScroll,
          onInterrupt: endProgrammaticScroll,
        });
      }

      prevBtns.forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (activeIndex > 0) goTo(activeIndex - 1);
          },
          { signal },
        );
      });

      nextBtns.forEach((btn) => {
        btn.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (activeIndex < count - 1) {
              goTo(activeIndex + 1);
            } else {
              goTo(0);
            }
          },
          { signal },
        );
      });

      renderStage(0);
      updateNav(0);

      return () => {
        ac.abort();
        st.kill();
        gsap.killTweensOf(window);
        endProgrammaticScroll();
        gsap.set(track, { clearProps: "transform" });
        resetMobileLayout();
        ScrollTrigger.refresh();
      };
    },
  });
}
